import React, { useState, useEffect, useCallback, useRef } from 'react';
import { MapContainer, TileLayer, Marker, Popup, useMapEvents, useMap } from 'react-leaflet';
import L from 'leaflet';
import 'leaflet/dist/leaflet.css';
import { useLanguage } from '../context/LanguageContext';
import { Search, Plus, X, MapPin, CheckCircle, AlertCircle, Loader2, ShoppingBag } from 'lucide-react';
import { API_URL } from '../config';



const texts = {
  ru: {
    title: 'Объекты', subtitle: 'Карта строительных объектов с бронированием',
    available: 'Свободен', reserved: 'Забронирован', sold: 'Реализован', mine: 'Моя бронь',
    book: 'Забронировать', unbook: 'Снять бронь', sell: 'Реализован',
    name: 'Название', address: 'Адрес', area: 'Площадь (м²)', description: 'Описание',
    deadline: 'Примерная дата реализации',
    create: 'Создать объект', creating: 'Создание...',
    addNew: 'Новый объект', cancel: 'Отмена',
    clickMap: 'Кликните на карту для выбора местоположения',
    reservedBy: 'Забронировал', reservedAt: 'Дата брони', createdBy: 'Создал',
    openYandex: 'Яндекс.Карты', open2gis: '2ГИС',
    successBook: 'Объект успешно забронирован!', successUnbook: 'Бронь снята',
    successSell: 'Объект отмечен как реализованный',
    filterAll: 'Все', filterAvailable: 'Свободные', filterMine: 'Мои', filterReserved: 'Забронированные', filterSold: 'Реализованные',
    noObjects: 'Объектов пока нет', search: 'Поиск по названию или адресу...',
    alreadyReserved: 'Объект уже забронирован другим дилером',
  },
  kz: {
    title: 'Нысандар', subtitle: 'Құрылыс нысандарының картасы',
    available: 'Бос', reserved: 'Брондалған', sold: 'Іске асырылған', mine: 'Менің брондау',
    book: 'Брондау', unbook: 'Брондан бас тарту', sell: 'Іске асырылды',
    name: 'Атауы', address: 'Мекенжай', area: 'Аудан (м²)', description: 'Сипаттама',
    deadline: 'Болжалды іске асыру күні',
    create: 'Нысан құру', creating: 'Құрылуда...',
    addNew: 'Жаңа нысан', cancel: 'Болдырмау',
    clickMap: 'Орынды таңдау үшін картаны басыңыз',
    reservedBy: 'Брондаған', reservedAt: 'Брондау күні', createdBy: 'Құрған',
    openYandex: 'Яндекс.Карта', open2gis: '2ГИС',
    successBook: 'Нысан сәтті брондалды!', successUnbook: 'Бронд жойылды',
    successSell: 'Нысан іске асырылды деп белгіленді',
    filterAll: 'Барлығы', filterAvailable: 'Бос', filterMine: 'Менікі', filterReserved: 'Брондалған', filterSold: 'Іске асырылған',
    noObjects: 'Нысандар жоқ', search: 'Атауы бойынша іздеу...',
    alreadyReserved: 'Нысан басқа дилермен брондалған',
  },
  en: {
    title: 'Objects', subtitle: 'Construction objects map with booking',
    available: 'Available', reserved: 'Reserved', sold: 'Completed', mine: 'My reservation',
    book: 'Reserve', unbook: 'Release', sell: 'Completed',
    name: 'Name', address: 'Address', area: 'Area (m²)', description: 'Description',
    deadline: 'Estimated completion date',
    create: 'Create Object', creating: 'Creating...',
    addNew: 'New Object', cancel: 'Cancel',
    clickMap: 'Click on the map to select location',
    reservedBy: 'Reserved by', reservedAt: 'Reserved at', createdBy: 'Created by',
    openYandex: 'Yandex Maps', open2gis: '2GIS',
    successBook: 'Object reserved!', successUnbook: 'Reservation released',
    successSell: 'Object marked as completed',
    filterAll: 'All', filterAvailable: 'Available', filterMine: 'Mine', filterReserved: 'Reserved', filterSold: 'Completed',
    noObjects: 'No objects yet', search: 'Search by name or address...',
    alreadyReserved: 'Object already reserved by another dealer',
  },
};

const createIcon = (color) => L.divIcon({
  className: 'custom-marker',
  html: `<div style="width:28px;height:28px;border-radius:50%;background:${color};border:3px solid white;box-shadow:0 2px 8px rgba(0,0,0,0.3);"></div>`,
  iconSize: [28, 28], iconAnchor: [14, 14], popupAnchor: [0, -14],
});

const icons = {
  available: createIcon('#22c55e'),
  mine: createIcon('#061d36'),
  reserved: createIcon('#8b4444'),
  sold: createIcon('#eab308'),
};

function MapClickHandler({ onClick }) {
  useMapEvents({ click: (e) => onClick(e.latlng) });
  return null;
}

function MapRefSetter({ mapRef }) {
  const map = useMap();
  useEffect(() => { mapRef.current = map; }, [map, mapRef]);
  return null;
}

const ObjectsMap = ({ token, userRole }) => {
  const { language } = useLanguage();
  const t = texts[language] || texts.ru;
  const isAdmin = userRole === 'admin';

  const [objects, setObjects] = useState([]);
  const [filter, setFilter] = useState('all');
  const [search, setSearch] = useState('');
  const [showForm, setShowForm] = useState(false);
  const [notification, setNotification] = useState(null);
  const [actionLoading, setActionLoading] = useState(null);
  const pollRef = useRef(null);

  const [formData, setFormData] = useState({ name: '', address: '', area_m2: 0, description: '', deadline: '', lat: 0, lng: 0 });
  const [formCreating, setFormCreating] = useState(false);
  const [mapClickMode, setMapClickMode] = useState(false);
  const [editModal, setEditModal] = useState(null);
  const [editData, setEditData] = useState({});
  const [editSuggestions, setEditSuggestions] = useState([]);
  const [showEditSuggestions, setShowEditSuggestions] = useState(false);
  const editSuggestTimeout = useRef(null);
  const [suggestions, setSuggestions] = useState([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const suggestTimeout = useRef(null);
  const mapRef = useRef(null);

  const [searchSuggestions, setSearchSuggestions] = useState([]);
  const [showSearchSuggestions, setShowSearchSuggestions] = useState(false);
  const searchSuggestTimeout = useRef(null);

  const headers = { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' };

  const handleSearchInput = (value) => {
    setSearch(value);
    if (searchSuggestTimeout.current) clearTimeout(searchSuggestTimeout.current);
    if (value.length < 3) { setSearchSuggestions([]); setShowSearchSuggestions(false); return; }
    searchSuggestTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/yandex/suggest?text=${encodeURIComponent(value)}&lang=${language}`);
        if (res.ok) {
          const data = await res.json();
          const items = (data.results || []).map(r => ({
            title: r.title?.text || '', subtitle: r.subtitle?.text || '',
            full_address: r.full_address || '',
          }));
          setSearchSuggestions(items);
          setShowSearchSuggestions(items.length > 0);
        }
      } catch {}
    }, 300);
  };

  const selectSearchSuggestion = async (s) => {
    const addr = s.full_address || `${s.title}, ${s.subtitle}`;
    setSearch('');
    setSearchSuggestions([]); setShowSearchSuggestions(false);
    // Open creation form with pre-filled address
    setShowForm(true); setMapClickMode(true);
    setFormData(p => ({...p, address: addr, name: '', area_m2: 0, description: '', deadline: ''}));
    try {
      const res = await fetch(`${API_URL}/api/yandex/geocode?address=${encodeURIComponent(addr)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.lat && data.lng) {
          setFormData(p => ({...p, lat: data.lat, lng: data.lng}));
          mapRef.current?.setView([data.lat, data.lng], 16);
        }
      }
    } catch {}
  };

  const geocodeAddress = async (addr) => {
    if (!addr || addr.length < 5) return;
    try {
      const res = await fetch(`${API_URL}/api/yandex/geocode?address=${encodeURIComponent(addr)}`);
      if (res.ok) {
        const data = await res.json();
        if (data.lat && data.lng) {
          setFormData(p => ({...p, lat: data.lat, lng: data.lng}));
          mapRef.current?.setView([data.lat, data.lng], 16);
        }
      }
    } catch {}
  };

  const geocodeTimeout = useRef(null);

  const handleAddressInput = (value) => {
    setFormData(p => ({...p, address: value}));
    if (suggestTimeout.current) clearTimeout(suggestTimeout.current);
    if (geocodeTimeout.current) clearTimeout(geocodeTimeout.current);
    if (value.length < 3) { setSuggestions([]); setShowSuggestions(false); return; }
    suggestTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/yandex/suggest?text=${encodeURIComponent(value)}&lang=${language}`);
        if (res.ok) {
          const data = await res.json();
          const items = (data.results || []).map(r => ({
            title: r.title?.text || '', subtitle: r.subtitle?.text || '',
            text: r.subtitle?.text || r.title?.text || '',
            full_address: r.full_address || '',
            lat: r.lat, lng: r.lng, need_geocode: r.need_geocode || false,
          }));
          setSuggestions(items);
          setShowSuggestions(items.length > 0);
        }
      } catch {}
    }, 300);
    // Auto-geocode after typing stops
    geocodeTimeout.current = setTimeout(() => geocodeAddress(value), 800);
  };

  const selectSuggestion = async (s) => {
    const addr = s.full_address || s.text || `${s.title}, ${s.subtitle}`;
    setFormData(p => ({...p, address: addr}));
    setSuggestions([]); setShowSuggestions(false);
    if (s.lat && s.lng && !s.need_geocode) {
      setFormData(p => ({...p, lat: s.lat, lng: s.lng}));
      mapRef.current?.setView([s.lat, s.lng], 16);
    } else {
      try {
        const res = await fetch(`${API_URL}/api/yandex/geocode?address=${encodeURIComponent(addr)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.lat && data.lng) {
            setFormData(p => ({...p, lat: data.lat, lng: data.lng}));
            mapRef.current?.setView([data.lat, data.lng], 16);
          }
        }
      } catch {}
    }
  };

  const fetchObjects = useCallback(async () => {
    try {
      const res = await fetch(`${API_URL}/api/objects`, { headers });
      if (res.ok) setObjects(await res.json());
    } catch {}
  }, [token]);

  useEffect(() => {
    fetchObjects();
    pollRef.current = setInterval(fetchObjects, 60000);
    return () => clearInterval(pollRef.current);
  }, [fetchObjects]);

  const showNotif = (type, msg) => {
    setNotification({ type, msg });
    setTimeout(() => setNotification(null), 3000);
  };

  const handleCreate = async (e) => {
    e.preventDefault();
    if (!formData.lat) return;
    setFormCreating(true);
    try {
      const res = await fetch(`${API_URL}/api/objects`, { method: 'POST', headers, body: JSON.stringify(formData) });
      if (res.ok) {
        showNotif('success', t.create);
        setShowForm(false); setMapClickMode(false);
        setFormData({ name: '', address: '', area_m2: 0, description: '', deadline: '', lat: 0, lng: 0 });
        fetchObjects();
      } else {
        const err = await res.json();
        showNotif('error', err.detail || 'Error');
      }
    } catch { showNotif('error', 'Network error'); }
    setFormCreating(false);
  };

  const handleBook = async (id) => {
    setActionLoading(id);
    try {
      const res = await fetch(`${API_URL}/api/objects/${id}/book`, { method: 'POST', headers });
      if (res.ok) { showNotif('success', t.successBook); fetchObjects(); }
      else { const err = await res.json(); showNotif('error', err.detail || t.alreadyReserved); }
    } catch { showNotif('error', 'Error'); }
    setActionLoading(null);
  };

  const handleUnbook = async (id) => {
    setActionLoading(id);
    try {
      const res = await fetch(`${API_URL}/api/objects/${id}/unbook`, { method: 'POST', headers });
      if (res.ok) { showNotif('success', t.successUnbook); fetchObjects(); }
      else { const err = await res.json(); showNotif('error', err.detail || 'Error'); }
    } catch { showNotif('error', 'Error'); }
    setActionLoading(null);
  };

  const handleSell = async (id) => {
    setActionLoading(id);
    try {
      const res = await fetch(`${API_URL}/api/objects/${id}/sell`, { method: 'POST', headers });
      if (res.ok) { showNotif('success', t.successSell); fetchObjects(); }
      else { const err = await res.json(); showNotif('error', err.detail || 'Error'); }
    } catch { showNotif('error', 'Error'); }
    setActionLoading(null);
  };

  const openEditModal = (obj) => {
    setEditData({ name: obj.name, address: obj.address, area_m2: obj.area_m2 || 0, description: obj.description || '', deadline: obj.deadline || '', lat: obj.lat, lng: obj.lng });
    setEditModal(obj.id);
  };

  const saveEdit = async () => {
    setActionLoading(editModal);
    try {
      const res = await fetch(`${API_URL}/api/objects/${editModal}`, { method: 'PUT', headers, body: JSON.stringify(editData) });
      if (res.ok) { showNotif('success', language === 'ru' ? 'Объект обновлён' : 'Object updated'); setEditModal(null); fetchObjects(); }
      else { const err = await res.json(); showNotif('error', err.detail || 'Error'); }
    } catch { showNotif('error', 'Error'); }
    setActionLoading(null);
  };

  const handleEditAddress = (value) => {
    setEditData(p => ({...p, address: value}));
    if (editSuggestTimeout.current) clearTimeout(editSuggestTimeout.current);
    if (value.length < 3) { setEditSuggestions([]); setShowEditSuggestions(false); return; }
    editSuggestTimeout.current = setTimeout(async () => {
      try {
        const res = await fetch(`${API_URL}/api/yandex/suggest?text=${encodeURIComponent(value)}&lang=${language}`);
        if (res.ok) {
          const data = await res.json();
          const items = (data.results || []).map(r => ({
            title: r.title?.text || '', subtitle: r.subtitle?.text || '',
            full_address: r.full_address || r.subtitle?.text || r.title?.text || '',
            lat: r.lat, lng: r.lng, need_geocode: r.need_geocode || false,
          }));
          setEditSuggestions(items);
          setShowEditSuggestions(items.length > 0);
        }
      } catch {}
    }, 300);
  };

  const selectEditSuggestion = async (s) => {
    const addr = s.full_address || `${s.title}, ${s.subtitle}`;
    setEditData(p => ({...p, address: addr}));
    setEditSuggestions([]); setShowEditSuggestions(false);
    if (s.lat && s.lng && !s.need_geocode) {
      setEditData(p => ({...p, lat: s.lat, lng: s.lng}));
    } else {
      try {
        const res = await fetch(`${API_URL}/api/yandex/geocode?address=${encodeURIComponent(addr)}`);
        if (res.ok) {
          const data = await res.json();
          if (data.lat && data.lng) setEditData(p => ({...p, lat: data.lat, lng: data.lng}));
        }
      } catch {}
    }
  };

  const fitToObjects = useCallback((objs) => {
    if (!mapRef.current || objs.length === 0) return;
    if (objs.length === 1) {
      mapRef.current.setView([objs[0].lat, objs[0].lng], 14);
    } else {
      const bounds = objs.map(o => [o.lat, o.lng]);
      mapRef.current.fitBounds(bounds, { padding: [40, 40], maxZoom: 14 });
    }
  }, []);

  const handleFilter = (key) => {
    setFilter(key);
    setSearch('');
    setAllSearch('');
    const objs = objects.filter(obj => {
      if (key === 'mine') return obj.perspective === 'mine';
      if (key === 'available') return obj.perspective === 'available';
      if (key === 'reserved') return obj.perspective === 'reserved' || obj.perspective === 'mine';
      if (key === 'sold') return obj.perspective === 'sold';
      return true;
    });
    fitToObjects(objs);
  };

  const [sectionSearch, setSectionSearch] = useState({ mine: '', reserved: '', available: '', sold: '' });
  const [allSearch, setAllSearch] = useState('');

  const filtered = objects.filter(obj => {
    if (filter === 'mine' && obj.perspective !== 'mine') return false;
    if (filter === 'available' && obj.perspective !== 'available') return false;
    if (filter === 'reserved' && obj.perspective !== 'reserved' && obj.perspective !== 'mine') return false;
    if (filter === 'sold' && obj.perspective !== 'sold') return false;
    if (search) {
      const s = search.toLowerCase();
      return obj.name.toLowerCase().includes(s) || obj.address.toLowerCase().includes(s);
    }
    return true;
  });

  const counts = {
    all: objects.length,
    available: objects.filter(o => o.perspective === 'available').length,
    mine: objects.filter(o => o.perspective === 'mine').length,
    reserved: objects.filter(o => o.perspective === 'reserved' || o.perspective === 'mine').length,
    sold: objects.filter(o => o.perspective === 'sold').length,
  };

  const statusLabel = (obj) => {
    if (obj.perspective === 'mine') return t.mine;
    if (obj.perspective === 'available') return t.available;
    if (obj.perspective === 'sold') return t.sold;
    return t.reserved;
  };

  const statusColor = (obj) => {
    if (obj.perspective === 'mine') return 'bg-[#061d36]';
    if (obj.perspective === 'available') return 'bg-green-500';
    if (obj.perspective === 'sold') return 'bg-yellow-500';
    return 'bg-[#8b4444]';
  };

  return (
    <>
    <div className="space-y-4" data-testid="objects-map">
      {notification && (
        <div className={`fixed top-4 right-4 z-50 flex items-center gap-2 px-5 py-3 rounded-xl shadow-lg text-sm font-medium animate-in slide-in-from-right ${notification.type === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          {notification.type === 'success' ? <CheckCircle className="w-4 h-4" /> : <AlertCircle className="w-4 h-4" />}
          {notification.msg}
        </div>
      )}

      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-2xl font-bold text-[#061d36]">{t.title}</h2>
          <p className="text-[#061d36]/50 text-sm">{t.subtitle}</p>
        </div>
        <button onClick={() => { setShowForm(!showForm); setMapClickMode(!showForm); }}
          className={`flex items-center gap-2 px-4 py-2.5 rounded-xl text-sm font-semibold transition-all ${showForm ? 'bg-[#061d36]/10 text-[#061d36]' : 'bg-[#8b4444] text-white hover:bg-[#7a3838]'}`}
          data-testid="add-object-btn">
          {showForm ? <><X className="w-4 h-4" />{t.cancel}</> : <><Plus className="w-4 h-4" />{t.addNew}</>}
        </button>
      </div>

      {/* Filters */}
      <div className="flex flex-wrap gap-2 items-center">
        {[
          { key: 'all', label: t.filterAll, count: counts.all },
          { key: 'available', label: t.filterAvailable, count: counts.available, color: 'bg-green-500' },
          { key: 'mine', label: t.filterMine, count: counts.mine, color: 'bg-[#061d36]' },
          { key: 'reserved', label: t.filterReserved, count: counts.reserved, color: 'bg-[#8b4444]' },
          { key: 'sold', label: t.filterSold, count: counts.sold, color: 'bg-yellow-500' },
        ].map(f => (
          <button key={f.key} onClick={() => handleFilter(f.key)}
            className={`flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-all ${filter === f.key ? 'bg-[#061d36] text-white' : 'bg-white text-[#061d36]/60 hover:bg-[#061d36]/5 border border-[#061d36]/10'}`}
            data-testid={`filter-${f.key}`}>
            {f.color && <div className={`w-2 h-2 rounded-full ${f.color}`} />}
            {f.label} ({f.count})
          </button>
        ))}
        <div className="flex-1 min-w-[200px] ml-2">
          <div className="relative" style={{zIndex: 1000}}>
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#061d36]/30" />
            <input type="text" value={search} onChange={e => handleSearchInput(e.target.value)}
              onFocus={() => searchSuggestions.length > 0 && setShowSearchSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSearchSuggestions(false), 300)}
              autoComplete="off"
              placeholder={t.search}
              className="w-full pl-9 pr-3 py-1.5 bg-white border border-[#061d36]/10 rounded-lg text-sm text-[#061d36] placeholder-[#061d36]/30 focus:outline-none focus:border-[#8b4444]"
              data-testid="objects-search" />
            {showSearchSuggestions && searchSuggestions.length > 0 && (
              <div className="absolute left-0 right-0 top-full mt-1 bg-white border border-[#061d36]/15 rounded-lg shadow-xl max-h-60 overflow-y-auto" style={{zIndex: 9999}}>
                {searchSuggestions.map((s, i) => (
                  <button key={i} type="button" onMouseDown={() => selectSearchSuggestion(s)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#8b4444]/10 transition-colors border-b border-[#061d36]/5 last:border-b-0">
                    <span className="font-medium text-[#061d36]">{s.title}</span>
                    {s.subtitle && <span className="text-[#061d36]/40 ml-1 text-xs">{s.subtitle}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Create Form */}
      {showForm && (
        <form onSubmit={handleCreate} className="bg-white rounded-xl border border-[#8b4444]/20 p-5 space-y-3" data-testid="create-object-form">
          <div>
            <label className="text-xs text-[#061d36]/60 mb-1 block">{t.name} *</label>
            <input required value={formData.name} onChange={e => setFormData(p => ({...p, name: e.target.value}))}
              className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
          </div>
          <div className="relative" style={{zIndex: 100}}>
            <label className="text-xs text-[#061d36]/60 mb-1 block">{t.address} *</label>
            <input required value={formData.address} onChange={e => handleAddressInput(e.target.value)}
              onFocus={() => suggestions.length > 0 && setShowSuggestions(true)}
              onBlur={() => setTimeout(() => setShowSuggestions(false), 300)}
              autoComplete="off"
              className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]"
              data-testid="address-input" />
            {showSuggestions && suggestions.length > 0 && (
              <div className="absolute z-[999] left-0 right-0 top-full mt-1 bg-white border border-[#061d36]/15 rounded-lg shadow-xl max-h-60 overflow-y-auto">
                {suggestions.map((s, i) => (
                  <button key={i} type="button" onMouseDown={() => selectSuggestion(s)}
                    className="w-full text-left px-3 py-2.5 text-sm hover:bg-[#8b4444]/10 transition-colors border-b border-[#061d36]/5 last:border-b-0">
                    <span className="font-medium text-[#061d36]">{s.title}</span>
                    {s.subtitle && <span className="text-[#061d36]/40 ml-1 text-xs">{s.subtitle}</span>}
                  </button>
                ))}
              </div>
            )}
          </div>
          <div className="grid sm:grid-cols-3 gap-3">
            <div>
              <label className="text-xs text-[#061d36]/60 mb-1 block">{t.area}</label>
              <input type="number" value={formData.area_m2 || ''} onChange={e => setFormData(p => ({...p, area_m2: parseFloat(e.target.value) || 0}))}
                className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
            </div>
            <div>
              <label className="text-xs text-[#061d36]/60 mb-1 block">{t.deadline}</label>
              <input type="date" value={formData.deadline} onChange={e => setFormData(p => ({...p, deadline: e.target.value}))}
                className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
            </div>
            <div>
              <label className="text-xs text-[#061d36]/60 mb-1 block">{t.description}</label>
              <input value={formData.description} onChange={e => setFormData(p => ({...p, description: e.target.value}))}
                className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
            </div>
          </div>
          {formData.lat ? (
            <p className="text-xs text-green-600">
              <MapPin className="w-3 h-3 inline" /> {formData.lat.toFixed(5)}, {formData.lng.toFixed(5)}
              <span className="text-[#061d36]/40 ml-2">
                {language === 'ru' ? '— перетащите маркер для точной позиции' : language === 'kz' ? '— маркерді дәл орынға жылжытыңыз' : '— drag marker for exact position'}
              </span>
            </p>
          ) : (
            <p className="text-xs text-[#8b4444]">{t.clickMap}</p>
          )}
          <button type="submit" disabled={formCreating || !formData.lat}
            className="px-6 py-2.5 bg-[#8b4444] text-white rounded-lg text-sm font-semibold hover:bg-[#7a3838] disabled:opacity-50 flex items-center gap-2"
            data-testid="create-object-submit">
            {formCreating ? <><Loader2 className="w-4 h-4 animate-spin" />{t.creating}</> : <>{language === 'ru' ? 'Создать и забронировать' : language === 'kz' ? 'Құру және брондау' : 'Create & Reserve'}</>}
          </button>
        </form>
      )}

      {/* Map */}
      <div className="rounded-xl overflow-hidden border border-[#061d36]/10 shadow-sm" style={{ height: '450px' }}>
        <MapContainer center={[51.13, 71.43]} zoom={6} style={{ height: '100%', width: '100%' }} scrollWheelZoom={true} attributionControl={false}>
          <MapRefSetter mapRef={mapRef} />
          <TileLayer
            attribution='&copy; <a href="https://2gis.com" target="_blank">2GIS</a>'
            url="https://tile{s}.maps.2gis.com/tiles?x={x}&y={y}&z={z}&v=1"
            subdomains={['0', '1', '2', '3']}
            maxZoom={18}
          />
          {mapClickMode && <MapClickHandler onClick={async (latlng) => {
            setFormData(p => ({...p, lat: latlng.lat, lng: latlng.lng}));
            try {
              const res = await fetch(`${API_URL}/api/yandex/reverse-geocode?lat=${latlng.lat}&lng=${latlng.lng}`);
              if (res.ok) {
                const data = await res.json();
                if (data.address) setFormData(p => ({...p, address: data.address}));
              }
            } catch {}
          }} />}
          {filtered.map(obj => (
            <Marker key={obj.id} position={[obj.lat, obj.lng]} icon={icons[obj.perspective] || icons.reserved}>
              <Popup minWidth={260} maxWidth={320}>
                <div className="text-sm space-y-2 p-1">
                  <div className="flex items-center gap-2">
                    <div className={`w-3 h-3 rounded-full flex-shrink-0 ${statusColor(obj)}`} />
                    <span className="font-bold text-[#061d36]">{obj.name}</span>
                  </div>
                  <p className="text-[#061d36]/60 text-xs">{obj.address}</p>
                  {obj.area_m2 > 0 && <p className="text-xs text-[#061d36]/50">{obj.area_m2} м²</p>}
                  {obj.description && <p className="text-xs text-[#061d36]/50">{obj.description}</p>}
                  {obj.deadline && <p className="text-xs text-[#061d36]/50">{t.deadline}: {new Date(obj.deadline).toLocaleDateString()}</p>}
                  <p className="text-xs font-medium">{statusLabel(obj)}</p>
                  {obj.reserved_by_name && obj.perspective !== 'available' && (
                    <p className="text-xs text-[#061d36]/50">{t.reservedBy}: <span className="font-medium">{obj.reserved_by_name}</span></p>
                  )}
                  {obj.created_by_name && (
                    <p className="text-xs text-[#061d36]/40">{t.createdBy}: {obj.created_by_name}</p>
                  )}
                  {obj.reserved_at && obj.perspective !== 'available' && (
                    <p className="text-xs text-[#061d36]/40">{t.reservedAt}: {new Date(obj.reserved_at).toLocaleDateString()}</p>
                  )}
                  <div className="flex gap-1 flex-wrap pt-1">
                    {obj.perspective === 'available' && (
                      <button onClick={() => handleBook(obj.id)} disabled={actionLoading === obj.id}
                        className="px-3 py-1 bg-green-600 text-white rounded text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                        data-testid={`book-btn-${obj.id}`}>
                        {actionLoading === obj.id ? '...' : t.book}
                      </button>
                    )}
                    {obj.perspective === 'mine' && (
                      <>
                        <button onClick={() => handleUnbook(obj.id)} disabled={actionLoading === obj.id}
                          className="px-3 py-1 bg-[#061d36] text-white rounded text-xs font-medium hover:bg-[#0d2a47] disabled:opacity-50">
                          {actionLoading === obj.id ? '...' : t.unbook}
                        </button>
                        <button onClick={() => handleSell(obj.id)} disabled={actionLoading === obj.id}
                          className="px-3 py-1 bg-yellow-500 text-white rounded text-xs font-medium hover:bg-yellow-600 disabled:opacity-50">
                          {actionLoading === obj.id ? '...' : t.sell}
                        </button>
                      </>
                    )}
                    {(obj.perspective === 'mine' || isAdmin) && (
                      <button onClick={() => openEditModal(obj)}
                        className="px-3 py-1 bg-[#061d36]/10 text-[#061d36] rounded text-xs font-medium hover:bg-[#061d36]/20"
                        data-testid={`edit-btn-${obj.id}`}>
                        {language === 'ru' ? 'Редактировать' : 'Edit'}
                      </button>
                    )}
                    <a href={`https://yandex.ru/maps/?pt=${obj.lng},${obj.lat}&z=16&l=map`} target="_blank" rel="noreferrer"
                      className="px-2 py-1 bg-[#061d36]/10 text-[#061d36] rounded text-xs hover:bg-[#061d36]/20">
                      {t.openYandex}
                    </a>
                    <a href={`https://2gis.kz/search/${obj.lat},${obj.lng}`} target="_blank" rel="noreferrer"
                      className="px-2 py-1 bg-[#061d36]/10 text-[#061d36] rounded text-xs hover:bg-[#061d36]/20">
                      {t.open2gis}
                    </a>
                  </div>
                </div>
              </Popup>
            </Marker>
          ))}
          {mapClickMode && formData.lat !== 0 && (
            <Marker position={[formData.lat, formData.lng]}
              draggable={true}
              eventHandlers={{
                dragend: async (e) => {
                  const pos = e.target.getLatLng();
                  setFormData(p => ({...p, lat: pos.lat, lng: pos.lng}));
                  try {
                    const res = await fetch(`${API_URL}/api/yandex/reverse-geocode?lat=${pos.lat}&lng=${pos.lng}`);
                    if (res.ok) {
                      const data = await res.json();
                      if (data.address) {
                        setFormData(p => ({...p, address: data.address}));
                      }
                    }
                  } catch {}
                },
              }}
              icon={L.divIcon({
              className: 'custom-marker',
              html: '<div style="width:32px;height:32px;border-radius:50%;background:#8b4444;border:3px solid white;box-shadow:0 2px 12px rgba(0,0,0,0.4);display:flex;align-items:center;justify-content:center;color:white;font-size:16px;cursor:grab;">+</div>',
              iconSize: [32, 32], iconAnchor: [16, 16],
            })} />
          )}
        </MapContainer>
      </div>

      {/* Object Lists by Section */}
      {filter === 'all' && (
        <div className="relative">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[#061d36]/25" />
          <input type="text" value={allSearch}
            onChange={e => setAllSearch(e.target.value)}
            placeholder={t.search}
            className="w-full pl-9 pr-3 py-2 bg-white border border-[#061d36]/10 rounded-lg text-sm text-[#061d36] placeholder-[#061d36]/25 focus:outline-none focus:border-[#8b4444]"
            data-testid="all-objects-search" />
        </div>
      )}
      {[
        { key: 'mine', label: t.filterMine, color: 'border-[#061d36]', dotColor: 'bg-[#061d36]', perspectives: ['mine'] },
        { key: 'reserved', label: t.filterReserved, color: 'border-[#8b4444]', dotColor: 'bg-[#8b4444]', perspectives: ['reserved'] },
        { key: 'available', label: t.filterAvailable, color: 'border-green-500', dotColor: 'bg-green-500', perspectives: ['available'] },
        { key: 'sold', label: t.filterSold, color: 'border-yellow-500', dotColor: 'bg-yellow-500', perspectives: ['sold'] },
      ].map(section => {
        const sectionObjs = objects.filter(o => section.perspectives.includes(o.perspective));
        if (sectionObjs.length === 0 && filter !== 'all' && filter !== section.key) return null;
        if (filter !== 'all' && filter !== section.key) return null;
        const searchVal = filter === 'all' ? allSearch : (sectionSearch[section.key] || '');
        const sectionFiltered = sectionObjs.filter(o => {
          if (!searchVal) return true;
          const s = searchVal.toLowerCase();
          return o.name.toLowerCase().includes(s) || o.address.toLowerCase().includes(s) || (o.reserved_by_name || '').toLowerCase().includes(s);
        });
        if (filter === 'all' && allSearch && sectionFiltered.length === 0) return null;
        return (
          <div key={section.key} className="space-y-2">
            <div className="flex items-center gap-3">
              <div className={`w-3 h-3 rounded-full ${section.dotColor}`} />
              <h3 className="text-sm font-bold text-[#061d36]">{section.label} ({sectionFiltered.length})</h3>
              {filter !== 'all' && (
              <div className="flex-1 relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-[#061d36]/25" />
                <input type="text" value={searchVal}
                  onChange={e => setSectionSearch(p => ({...p, [section.key]: e.target.value}))}
                  placeholder={t.search}
                  className="w-full pl-8 pr-3 py-1 bg-white border border-[#061d36]/10 rounded-lg text-xs text-[#061d36] placeholder-[#061d36]/25 focus:outline-none focus:border-[#8b4444]" />
              </div>
              )}
            </div>
            {sectionFiltered.length === 0 && (
              <div className="text-center py-3 text-[#061d36]/20 text-xs">{t.noObjects}</div>
            )}
            {sectionFiltered.map(obj => (
              <div key={obj.id} onClick={() => mapRef.current?.setView([obj.lat, obj.lng], 16)}
                className={`bg-white rounded-xl border p-4 flex items-center gap-4 transition-all cursor-pointer hover:shadow-md ${
                obj.perspective === 'mine' ? 'border-[#061d36]/40' : obj.perspective === 'sold' ? 'border-yellow-400/40' : obj.perspective === 'available' ? 'border-green-300/40' : 'border-[#8b4444]/40'
              }`} data-testid={`object-card-${obj.id}`}>
                <div className={`w-3 h-3 rounded-full flex-shrink-0 ${statusColor(obj)}`} />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className="font-semibold text-[#061d36] truncate">{obj.name}</span>
                    <span className={`text-[10px] px-1.5 py-0.5 rounded-full font-medium ${
                      obj.perspective === 'available' ? 'bg-green-100 text-green-700' :
                      obj.perspective === 'sold' ? 'bg-yellow-100 text-yellow-700' :
                      obj.perspective === 'mine' ? 'bg-[#061d36]/10 text-[#061d36]' :
                      'bg-[#8b4444]/10 text-[#8b4444]'
                    }`}>{statusLabel(obj)}</span>
                  </div>
                  <p className="text-xs text-[#061d36]/50 truncate">{obj.address}</p>
                  {obj.reserved_at && obj.perspective !== 'available' && (
                    <p className="text-xs text-[#061d36]/30">{t.reservedAt}: {new Date(obj.reserved_at).toLocaleDateString()}</p>
                  )}
                </div>
                {obj.reserved_by_name && obj.perspective !== 'available' && (
                  <div className="flex-shrink-0 text-right min-w-[120px]">
                    <p className="text-[10px] text-[#061d36]/40">{t.reservedBy}</p>
                    <p className="text-xs font-semibold text-[#061d36] truncate">{obj.reserved_by_name}</p>
                  </div>
                )}
                {obj.area_m2 > 0 && <span className="text-sm text-[#061d36] font-semibold flex-shrink-0">{obj.area_m2} м²</span>}
                <div className="flex gap-1 flex-shrink-0">
                  {obj.perspective === 'available' && (
                    <button onClick={(e) => { e.stopPropagation(); handleBook(obj.id); }} disabled={actionLoading === obj.id}
                      className="px-3 py-1.5 bg-green-600 text-white rounded-lg text-xs font-medium hover:bg-green-700 disabled:opacity-50"
                      data-testid={`book-btn-${obj.id}`}>
                      {actionLoading === obj.id ? <Loader2 className="w-3 h-3 animate-spin" /> : t.book}
                    </button>
                  )}
                  {obj.perspective === 'mine' && (
                    <>
                      <button onClick={(e) => { e.stopPropagation(); handleUnbook(obj.id); }} disabled={actionLoading === obj.id}
                        className="px-3 py-1.5 bg-[#061d36] text-white rounded-lg text-xs font-medium hover:bg-[#0d2a47] disabled:opacity-50"
                        data-testid={`unbook-btn-${obj.id}`}>
                        {actionLoading === obj.id ? <Loader2 className="w-3 h-3 animate-spin" /> : t.unbook}
                      </button>
                      <button onClick={(e) => { e.stopPropagation(); handleSell(obj.id); }} disabled={actionLoading === obj.id}
                        className="px-3 py-1.5 bg-yellow-500 text-white rounded-lg text-xs font-medium hover:bg-yellow-600 disabled:opacity-50"
                        data-testid={`sell-btn-${obj.id}`}>
                        {actionLoading === obj.id ? <Loader2 className="w-3 h-3 animate-spin" /> : t.sell}
                      </button>
                    </>
                  )}
                  {(obj.perspective === 'mine' || isAdmin) && (
                    <button onClick={(e) => { e.stopPropagation(); openEditModal(obj); }}
                      className="px-3 py-1.5 bg-[#061d36]/10 text-[#061d36] rounded-lg text-xs font-medium hover:bg-[#061d36]/20"
                      data-testid={`edit-list-btn-${obj.id}`}>
                      {language === 'ru' ? 'Ред.' : 'Edit'}
                    </button>
                  )}
                  {obj.perspective === 'reserved' && (
                    <span className="px-3 py-1.5 bg-[#8b4444]/10 text-[#8b4444] border border-[#8b4444]/30 rounded-lg text-xs font-medium">{t.reserved}</span>
                  )}
                  {obj.perspective === 'sold' && (
                    <span className="px-3 py-1.5 bg-yellow-100 text-yellow-700 border border-yellow-400/30 rounded-lg text-xs font-medium">{t.sold}</span>
                  )}
                </div>
              </div>
            ))}
          </div>
        );
      })}
    </div>

    {/* Edit Object Modal */}
    {editModal && (
      <div className="fixed inset-0 z-[1000] flex items-center justify-center bg-black/50 backdrop-blur-sm px-4"
        onMouseDown={e => { if (e.target === e.currentTarget) { setEditModal(null); setShowEditSuggestions(false); } }}>
        <div className="bg-white rounded-2xl p-6 w-full max-w-md shadow-2xl">
          <h3 className="text-lg font-bold text-[#061d36] mb-4">{language === 'ru' ? 'Редактировать объект' : 'Edit Object'}</h3>
          <div className="space-y-3">
            <div>
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{t.name} *</label>
              <input value={editData.name || ''} onChange={e => setEditData(p => ({...p, name: e.target.value}))}
                className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
            </div>
            <div className="relative">
              <label className="text-xs text-[#061d36] font-semibold mb-1 block">{t.address} *</label>
              <input value={editData.address || ''} onChange={e => handleEditAddress(e.target.value)}
                onFocus={() => { if (editSuggestions.length > 0) setShowEditSuggestions(true); }}
                className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
              {showEditSuggestions && editSuggestions.length > 0 && (
                <div className="absolute z-50 left-0 right-0 top-full mt-1 bg-white border border-[#061d36]/15 rounded-lg shadow-xl max-h-48 overflow-y-auto">
                  {editSuggestions.map((s, i) => (
                    <button key={i} onClick={() => selectEditSuggestion(s)}
                      className="w-full text-left px-3 py-2 text-sm hover:bg-[#8b4444]/5 border-b border-[#061d36]/5 last:border-b-0 transition-colors">
                      <span className="font-medium text-[#061d36]">{s.title}</span>
                      {s.subtitle && <span className="text-[#061d36]/50 ml-1 text-xs">{s.subtitle}</span>}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div>
                <label className="text-xs text-[#061d36] font-semibold mb-1 block">{t.area}</label>
                <input type="number" value={editData.area_m2 || ''} onChange={e => setEditData(p => ({...p, area_m2: parseFloat(e.target.value) || 0}))}
                  className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
              </div>
              <div>
                <label className="text-xs text-[#061d36] font-semibold mb-1 block">{t.deadline}</label>
                <input type="date" value={editData.deadline || ''} onChange={e => setEditData(p => ({...p, deadline: e.target.value}))}
                  className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
              </div>
              <div>
                <label className="text-xs text-[#061d36] font-semibold mb-1 block">{t.description}</label>
                <input value={editData.description || ''} onChange={e => setEditData(p => ({...p, description: e.target.value}))}
                  className="w-full px-3 py-2 border border-[#061d36]/15 rounded-lg text-sm focus:outline-none focus:border-[#8b4444]" />
              </div>
            </div>
            <p className="text-[10px] text-[#061d36]/40">{language === 'ru' ? 'Координаты' : 'Coordinates'}: {editData.lat?.toFixed(4)}, {editData.lng?.toFixed(4)}</p>
          </div>
          <div className="flex gap-2 mt-5">
            <button onClick={() => { setEditModal(null); setShowEditSuggestions(false); }}
              className="flex-1 py-2.5 text-sm text-[#061d36]/50 hover:text-[#061d36] transition-colors border border-[#061d36]/10 rounded-lg">
              {language === 'ru' ? 'Отмена' : 'Cancel'}
            </button>
            <button onClick={saveEdit} disabled={actionLoading === editModal || !editData.name || !editData.address}
              data-testid="save-edit-object"
              className="flex-1 py-2.5 bg-[#061d36] text-white rounded-lg text-sm font-semibold hover:bg-[#0d2a47] disabled:opacity-50 transition-all">
              {actionLoading === editModal ? '...' : (language === 'ru' ? 'Сохранить' : 'Save')}
            </button>
          </div>
        </div>
      </div>
    )}
  </>
  );
};

export default ObjectsMap;
